import { useEffect, useState } from "react";
import { api, notifyError } from "../lib/api";
import { fmtMoney } from "../lib/currency";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { toast } from "sonner";
import { Wallet, TrendingUp, Users, BarChart3, Trophy, MapPin, FileDown, Loader2, Eye } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { generateSalesStockReportPdf, generateStoragesListPdf } from "../lib/pdf";
import { paymentMethodLabel } from "../lib/paymentMethods";
import Combobox from "../components/Combobox";
import ProductViewDialog from "../components/ProductViewDialog";
import { tyreSummary } from "../lib/tyres";

function fmtTRY(n) {
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);
}

const COLORS = ["#0EA5E9", "#0F172A", "#16A34A", "#DC2626", "#2563EB"];

export default function Reports() {
  const { defaultCurrency, user, salesStockEnabled } = useAuth();
  const fmt = (n) => fmtMoney(n, defaultCurrency, { maximumFractionDigits: 0 });
  const today = new Date().toISOString().slice(0, 10);
  const yearAgo = new Date(Date.now() - 365 * 86400 * 1000).toISOString().slice(0, 10);
  const [dfrom, setDfrom] = useState(yearAgo);
  const [dto, setDto] = useState(today);
  const [revenue, setRevenue] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [topCount, setTopCount] = useState([]);
  const [topAmount, setTopAmount] = useState([]);
  const [staff, setStaff] = useState([]);
  const [occupancy, setOccupancy] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storages, setStorages] = useState([]);
  const [storagesStatus, setStoragesStatus] = useState("depoda");
  const [storagesSeason, setStoragesSeason] = useState("all");
  // Emanet raporu müşteri filtresi — backend'e customer_id olarak geçer.
  const [storagesCustomer, setStoragesCustomer] = useState("");
  const [checkinFrom, setCheckinFrom] = useState("");
  const [checkinTo, setCheckinTo] = useState("");
  const [checkoutFrom, setCheckoutFrom] = useState("");
  const [checkoutTo, setCheckoutTo] = useState("");
  const [storagesLoading, setStoragesLoading] = useState(false);
  const [storagesReload, setStoragesReload] = useState(0);
  const [pdfBusy, setPdfBusy] = useState(false);

  // --- Satış/Stok raporu (şartname #23) ---
  const [ssData, setSsData] = useState(null);
  const [ssLoading, setSsLoading] = useState(false);
  const [ssPdfBusy, setSsPdfBusy] = useState(false);
  const [ssType, setSsType] = useState("all");
  const [ssCondition, setSsCondition] = useState("all");
  // Üretim Yılı YALNIZCA Lastik tipinde anlamlıdır (ürün alanı lastiğe özel).
  const [ssYear, setSsYear] = useState("");
  const [ssExitType, setSsExitType] = useState("all");
  const [ssStockStatus, setSsStockStatus] = useState("all");
  const [ssWarehouse, setSsWarehouse] = useState("all");
  // Satıcı = stok GİRİŞİ tarafı, Müşteri = stok ÇIKIŞI (satış) tarafı
  const [ssSupplier, setSsSupplier] = useState("all");
  const [ssCustomer, setSsCustomer] = useState("all");
  const [ssFrom, setSsFrom] = useState("");
  const [ssTo, setSsTo] = useState("");
  const [ssWarehouses, setSsWarehouses] = useState([]);
  const [ssSuppliers, setSsSuppliers] = useState([]);
  // Görüntüle: rapor satırı ürünün tüm alanlarını taşımaz, tıklanınca mevcut
  // ürün ucundan çekilir. `ssMeta` kategori/sezon etiketleri için.
  const [ssMeta, setSsMeta] = useState({});
  const [ssViewProduct, setSsViewProduct] = useState(null);
  const [ssViewLoadingId, setSsViewLoadingId] = useState(null);
  // Müşteri listesi HER İKİ sekme tarafından kullanılır (Emanet raporu
  // müşteri filtresi + Satış/Stok filtresi) → Satış/Stok modülünden BAĞIMSIZ
  // yüklenir; aksi hâlde modül kapalı firmalarda Emanet filtresi boş kalırdı.
  const [customers, setCustomers] = useState([]);
  const [ssReload, setSsReload] = useState(0);

  const loadSalesStock = async () => {
    if (!salesStockEnabled) return;
    setSsLoading(true);
    try {
      const p = new URLSearchParams();
      if (ssType !== "all") p.set("product_type", ssType);
      if (ssCondition !== "all") p.set("condition", ssCondition);
      // Yıl yalnızca Lastik seçiliyken gönderilir — filtre de yalnızca o
      // durumda görünür, aksi hâlde gizlenen bir filtre sonucu daraltırdı.
      if (ssType === "lastik" && /^\d{4}$/.test(ssYear)) p.set("production_year", ssYear);
      if (ssExitType !== "all") p.set("exit_type", ssExitType);
      if (ssStockStatus !== "all") p.set("stock_status", ssStockStatus);
      if (ssWarehouse !== "all") p.set("warehouse_id", ssWarehouse);
      if (ssSupplier !== "all") p.set("supplier_id", ssSupplier);
      if (ssCustomer !== "all") p.set("customer_id", ssCustomer);
      if (ssFrom) p.set("dfrom", ssFrom);
      if (ssTo) p.set("dto", `${ssTo}T23:59:59`);
      const qs = p.toString();
      const { data } = await api.get(`/reports/sales-stock${qs ? `?${qs}` : ""}`);
      setSsData(data);
    } catch (e) {
      notifyError(e);
    } finally {
      setSsLoading(false);
    }
  };

  useEffect(() => {
    if (!salesStockEnabled) return;
    (async () => {
      // Filtre listeleri opsiyoneldir — biri gelmezse rapor yine çalışır.
      const [w, s, m] = await Promise.allSettled([
        api.get("/warehouses"),
        api.get("/sales-stock/suppliers"),
        api.get("/sales-stock/meta"),
      ]);
      if (w.status === "fulfilled") setSsWarehouses(w.value.data);
      if (s.status === "fulfilled") setSsSuppliers(s.value.data);
      if (m.status === "fulfilled") setSsMeta(m.value.data);
    })();
    // eslint-disable-next-line
  }, [salesStockEnabled]);

  useEffect(() => {
    loadSalesStock();
    // eslint-disable-next-line
  }, [salesStockEnabled, ssReload]);

  // Müşteri listesi — modül durumundan bağımsız, bir kez.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/customers");
        if (!cancelled) setCustomers(data);
      } catch {
        // Filtre listesi opsiyoneldir; gelmezse rapor yine çalışır.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const supplierItems = [
    { value: "all", label: "Tümü", keywords: ["tümü", "tumu"] },
    ...ssSuppliers.map((s) => ({
      value: s.id,
      label: s.phone ? `${s.full_name} — ${s.phone}` : s.full_name,
      keywords: [s.full_name, s.phone, s.tax_no].filter(Boolean),
    })),
  ];
  const customerItems = [
    { value: "all", label: "Tümü", keywords: ["tümü", "tumu"] },
    ...customers.map((c) => ({
      value: c.id,
      label: `${c.full_name} — ${c.phone}`,
      keywords: [c.full_name, c.phone, ...(c.vehicles || []).map((v) => v.plate)].filter(Boolean),
    })),
  ];

  const ssFilterLabel = () => {
    const parts = [];
    const typeLabels = { lastik: "Lastik", jant: "Jant", diger: "Diğer Malzeme" };
    const exitLabels = { satis: "Satış", iade: "Ürün İade", kullanim: "Kullanım" };
    if (ssType !== "all") parts.push(typeLabels[ssType]);
    if (ssCondition !== "all")
      parts.push(ssCondition === "sifir" ? "Sıfır" : "İkinci El");
    // PDF başlığı UYGULANAN filtreleri özetler; yıl yalnızca gönderildiği
    // koşulda yazılır (bkz. loadSalesStock).
    if (ssType === "lastik" && /^\d{4}$/.test(ssYear))
      parts.push(`Üretim Yılı: ${ssYear}`);
    if (ssExitType !== "all") parts.push(exitLabels[ssExitType]);
    if (ssStockStatus !== "all") parts.push(`Stok: ${ssStockStatus === "var" ? "Var" : "Yok"}`);
    if (ssWarehouse !== "all") {
      const w = ssWarehouses.find((x) => x.id === ssWarehouse);
      if (w) parts.push(w.name);
    }
    if (ssSupplier !== "all") {
      const s = ssSuppliers.find((x) => x.id === ssSupplier);
      if (s) parts.push(`Satıcı: ${s.full_name}`);
    }
    if (ssCustomer !== "all") {
      const c = customers.find((x) => x.id === ssCustomer);
      if (c) parts.push(`Müşteri: ${c.full_name}`);
    }
    if (ssFrom || ssTo) parts.push(`${ssFrom || "…"} – ${ssTo || "…"}`);
    return parts.join(" / ") || "Tümü";
  };

  /** Rapor satırındaki göz ikonu → Satış/Stok ile AYNI görüntüleme ekranı.
   *  Ürünün tamamı mevcut `/sales-stock/products/{id}` ucundan okunur; yetki
   *  ve tenant kontrolü orada yapılır (require_sales_module + tenant_id). */
  const openSsProduct = async (productId) => {
    setSsViewLoadingId(productId);
    try {
      const { data } = await api.get(`/sales-stock/products/${productId}`);
      setSsViewProduct(data);
    } catch (e) {
      notifyError(e);
    } finally {
      setSsViewLoadingId(null);
    }
  };

  const downloadSalesStockPdf = async () => {
    if (!ssData) return;
    setSsPdfBusy(true);
    try {
      await generateSalesStockReportPdf({
        rows: ssData.rows || [],
        totals: ssData.totals || {},
        collections: ssData.collections || [],
        tenantName: user?.tenant_name,
        defaultCurrency,
        filterLabel: ssFilterLabel(),
      });
    } catch (e) {
      toast.error("PDF oluşturulamadı");
    } finally {
      setSsPdfBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const dq = `dfrom=${dfrom}&dto=${dto}T23:59:59`;
      const [r, s, tc, ta, sp, oc, sm] = await Promise.all([
        api.get(`/reports/revenue?${dq}`),
        api.get(`/reports/seasons?${dq}`),
        api.get(`/reports/top-customers?metric=count&limit=5&${dq}`),
        api.get(`/reports/top-customers?metric=amount&limit=5&${dq}`),
        api.get(`/reports/staff-performance?${dq}`),
        api.get("/reports/occupancy"),
        api.get("/reports/summary"),
      ]);
      setRevenue(r.data);
      setSeasons(s.data);
      setTopCount(tc.data);
      setTopAmount(ta.data);
      setStaff(sp.data);
      setOccupancy(oc.data);
      setSummary(sm.data);
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const loadStorages = async () => {
    setStoragesLoading(true);
    try {
      const p = new URLSearchParams();
      if (storagesStatus && storagesStatus !== "all") p.set("status", storagesStatus);
      if (storagesSeason && storagesSeason !== "all") p.set("season", storagesSeason);
      if (storagesCustomer) p.set("customer_id", storagesCustomer);
      if (checkinFrom) p.set("checkin_from", checkinFrom);
      if (checkinTo) p.set("checkin_to", `${checkinTo}T23:59:59`);
      if (checkoutFrom) p.set("checkout_from", checkoutFrom);
      if (checkoutTo) p.set("checkout_to", `${checkoutTo}T23:59:59`);
      const qs = p.toString();
      const { data } = await api.get(`/storages${qs ? `?${qs}` : ""}`);
      setStorages(data);
    } catch (e) {
      notifyError(e);
    } finally {
      setStoragesLoading(false);
    }
  };

  // Reload when the status tab changes or filters are explicitly applied/cleared.
  useEffect(() => {
    loadStorages();
    /* eslint-disable-next-line */
  }, [storagesStatus, storagesReload]);

  const applyStorageFilters = () => setStoragesReload((t) => t + 1);
  const clearStorageFilters = () => {
    setStoragesSeason("all");
    setStoragesCustomer("");
    setCheckinFrom("");
    setCheckinTo("");
    setCheckoutFrom("");
    setCheckoutTo("");
    setStoragesReload((t) => t + 1);
  };
  const hasStorageFilters =
    storagesSeason !== "all" || storagesCustomer ||
    checkinFrom || checkinTo || checkoutFrom || checkoutTo;

  const dateColLabel =
    storagesStatus === "teslim_edildi"
      ? "Çıkış"
      : storagesStatus === "all"
      ? "Giriş/Çıkış"
      : "Giriş";

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");
  const storageDateCell = (s) => {
    if (storagesStatus === "teslim_edildi") return fmtDate(s.check_out_date);
    if (storagesStatus === "all") {
      return s.check_out_date
        ? `${fmtDate(s.check_in_date)} / ${fmtDate(s.check_out_date)}`
        : fmtDate(s.check_in_date);
    }
    return fmtDate(s.check_in_date);
  };

  const statusLabelFor = (s) =>
    s === "depoda" ? "Depodakiler" : s === "teslim_edildi" ? "Teslim Edilenler" : "Tümü";

  const downloadStoragesPdf = async () => {
    setPdfBusy(true);
    try {
      await generateStoragesListPdf({
        rows: storages,
        tenantName: user?.tenant_name,
        defaultCurrency,
        statusLabel: statusLabelFor(storagesStatus),
      });
    } catch (e) {
      toast.error("PDF oluşturulamadı");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-amber-600">Analiz</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Raporlar</h1>
          <p className="text-sm text-muted-foreground mt-2">Gelir, sezon, müşteri ve personel performansı.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="label-eyebrow">Başlangıç</Label>
            <Input type="date" value={dfrom} onChange={(e) => setDfrom(e.target.value)} className="rounded-sm h-10 w-40" data-testid="reports-from" />
          </div>
          <div>
            <Label className="label-eyebrow">Bitiş</Label>
            <Input type="date" value={dto} onChange={(e) => setDto(e.target.value)} className="rounded-sm h-10 w-40" data-testid="reports-to" />
          </div>
          <Button onClick={load} className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-10" data-testid="reports-apply">Uygula</Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Son 30 Gün Gelir" value={fmt(summary?.last_30_revenue)} icon={Wallet} accent />
        <KPI label="Son 30 Gün Tahsilat" value={summary?.last_30_payments || 0} icon={TrendingUp} />
        <KPI label="Aktif Lastik (Adet)" value={occupancy?.total_quantity || 0} icon={MapPin} />
        <KPI label="Aktif Emanet" value={occupancy?.total_storages || 0} icon={BarChart3} />
      </div>

      <Tabs defaultValue="revenue">
        <TabsList className="rounded-sm bg-muted">
          <TabsTrigger value="revenue" className="rounded-sm" data-testid="tab-revenue">Gelir</TabsTrigger>
          <TabsTrigger value="seasons" className="rounded-sm" data-testid="tab-seasons">Sezonlar</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-sm" data-testid="tab-customers">Müşteriler</TabsTrigger>
          <TabsTrigger value="staff" className="rounded-sm" data-testid="tab-staff">Personel</TabsTrigger>
          <TabsTrigger value="occupancy" className="rounded-sm" data-testid="tab-occupancy">Doluluk</TabsTrigger>
          <TabsTrigger value="storages" className="rounded-sm" data-testid="tab-storages">Emanetler</TabsTrigger>
          {/* Şartname #23: "Emanetler" sekmesinin YANINDA Satış/Stok */}
          {salesStockEnabled && (
            <TabsTrigger value="sales-stock" className="rounded-sm" data-testid="tab-sales-stock">
              Satış/Stok
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="revenue" className="mt-4">
          <Card className="rounded-sm border-border p-5">
            <div className="label-eyebrow mb-4">Aylık Gelir</div>
            {loading ? <Skeleton className="h-72 rounded-sm" /> : revenue.length === 0 ? (
              <Empty msg="Bu tarih aralığında ödeme yok" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#64748B" />
                  <YAxis stroke="#64748B" />
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 4, border: "1px solid #CBD5E1" }} />
                  <Line type="monotone" dataKey="total" stroke="#0EA5E9" strokeWidth={3} dot={{ fill: "#0F172A", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="seasons" className="mt-4">
          <Card className="rounded-sm border-border p-5">
            <div className="label-eyebrow mb-4">Sezon Dağılımı</div>
            {loading ? <Skeleton className="h-72 rounded-sm" /> : seasons.length === 0 ? (
              <Empty msg="Henüz emanet yok" />
            ) : (
              <div className="grid lg:grid-cols-2 gap-6 items-center">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={seasons.map(s => ({ name: seasonLabel(s.season), value: s.qty }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={50} label>
                      {seasons.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Legend />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {seasons.map((s, i) => (
                    <div key={s.season} className="flex items-center justify-between border border-border rounded-sm p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="font-semibold">{seasonLabel(s.season)}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold">{s.qty} adet</div>
                        <div className="text-xs text-muted-foreground">{s.count} kayıt</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="customers" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="rounded-sm border-border p-5">
              <div className="label-eyebrow mb-4 flex items-center gap-1.5"><Trophy className="h-3 w-3" /> En Çok Emanet (Adet)</div>
              {topCount.length === 0 ? <Empty msg="Veri yok" /> : (
                <ol className="space-y-2">
                  {topCount.map((c, i) => (
                    <li key={c.customer_id} className="flex items-center justify-between gap-3 border-b border-border pb-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="font-display font-black text-2xl text-amber-600 w-6">{i + 1}</div>
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{c.customer_name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{c.phone}</div>
                        </div>
                      </div>
                      <div className="font-display font-bold text-xl">{c.value}</div>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
            <Card className="rounded-sm border-border p-5">
              <div className="label-eyebrow mb-4 flex items-center gap-1.5"><Trophy className="h-3 w-3" /> En Çok Ödeme (Tutar)</div>
              {topAmount.length === 0 ? <Empty msg="Veri yok" /> : (
                <ol className="space-y-2">
                  {topAmount.map((c, i) => (
                    <li key={c.customer_id} className="flex items-center justify-between gap-3 border-b border-border pb-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="font-display font-black text-2xl text-amber-600 w-6">{i + 1}</div>
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{c.customer_name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{c.phone}</div>
                        </div>
                      </div>
                      <div className="font-display font-bold text-base">{fmt(c.value)}</div>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="staff" className="mt-4">
          <Card className="rounded-sm border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-foreground text-background">
                  <tr>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Personel</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Giriş</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Çıkış</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Ödeme Adet</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Ödeme Toplam</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {staff.length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center text-muted-foreground text-sm">Veri yok</td></tr>
                  ) : staff.map((s) => (
                    <tr key={s.actor_id} data-testid={`staff-perf-${s.actor_id}`}>
                      <td className="px-4 py-3 font-semibold">{s.actor_name}</td>
                      <td className="px-4 py-3 font-mono">{s.giris}</td>
                      <td className="px-4 py-3 font-mono">{s.cikis}</td>
                      <td className="px-4 py-3 font-mono">{s.payment_count}</td>
                      <td className="px-4 py-3 font-mono">{fmt(s.payment_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="occupancy" className="mt-4 space-y-4">
          <Card className="rounded-sm border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="label-eyebrow">Depo Bazlı Doluluk</div>
              {occupancy && (
                <div className="text-xs text-muted-foreground font-mono">
                  Toplam: <b>{occupancy.total_quantity || 0}</b> / {occupancy.total_capacity || 0} adet
                </div>
              )}
            </div>
            {!occupancy || !occupancy.by_warehouse || occupancy.by_warehouse.length === 0 ? (
              <Empty msg="Henüz depo veya aktif emanet yok" />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={Math.max(220, occupancy.by_warehouse.length * 56)}>
                  <BarChart data={occupancy.by_warehouse} layout="vertical" margin={{ left: 32 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" stroke="#64748B" />
                    <YAxis type="category" dataKey="name" stroke="#64748B" width={100} />
                    <Tooltip
                      contentStyle={{ borderRadius: 4 }}
                      formatter={(v, k) => {
                        if (k === "qty") return [`${v} adet`, "Dolu"];
                        if (k === "capacity") return [`${v} adet`, "Kapasite"];
                        return v;
                      }}
                    />
                    <Legend />
                    <Bar dataKey="capacity" name="Kapasite" fill="#E2E8F0" />
                    <Bar dataKey="qty" name="Dolu" fill="#F59E0B" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {occupancy.by_warehouse.map((w) => {
                    const pct = w.capacity > 0 ? Math.min(100, (w.qty / w.capacity) * 100) : 0;
                    const over = w.capacity > 0 && w.qty > w.capacity;
                    return (
                      <div key={w.warehouse_id || w.name} className="border border-border rounded-sm p-3" data-testid={`occ-wh-${w.warehouse_id || w.name}`}>
                        <div className="flex items-center justify-between text-sm">
                          <div className="font-semibold">{w.name}</div>
                          <div className="font-mono text-xs">
                            <b>{w.qty}</b> / {w.capacity || "—"} adet ({w.count} emanet)
                          </div>
                        </div>
                        <div className="h-2 w-full bg-muted rounded-sm mt-2 overflow-hidden">
                          <div
                            className={`h-full ${over ? "bg-red-500" : pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`}
                            style={{ width: `${over ? 100 : pct}%` }}
                          />
                        </div>
                        {over && (
                          <div className="text-xs text-red-600 mt-1">Kapasite aşıldı!</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>

          <Card className="rounded-sm border-border p-5">
            <div className="label-eyebrow mb-4">Raf Bazlı Doluluk (Adet)</div>
            {!occupancy || occupancy.by_location.length === 0 ? <Empty msg="Aktif emanet yok" /> : (
              <ResponsiveContainer width="100%" height={Math.max(220, occupancy.by_location.length * 36)}>
                <BarChart data={occupancy.by_location} layout="vertical" margin={{ left: 32 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" stroke="#64748B" />
                  <YAxis type="category" dataKey="location" stroke="#64748B" width={80} />
                  <Tooltip contentStyle={{ borderRadius: 4 }} />
                  <Bar dataKey="qty" fill="#0EA5E9" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="storages" className="mt-4">
          <Card className="rounded-sm border-border overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div>
                  <div className="label-eyebrow">Emanetler</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {storagesLoading ? "Yükleniyor…" : `${storages.length} kayıt`}
                  </div>
                </div>
                <Select value={storagesStatus} onValueChange={setStoragesStatus}>
                  <SelectTrigger className="w-44 rounded-sm h-9" data-testid="storages-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="depoda">Depodakiler</SelectItem>
                    <SelectItem value="teslim_edildi">Teslim Edilenler</SelectItem>
                    <SelectItem value="all">Tümü</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={downloadStoragesPdf}
                disabled={pdfBusy || storagesLoading || storages.length === 0}
                className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                data-testid="storages-pdf-btn"
              >
                {pdfBusy ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4 mr-2" />
                )}
                PDF İndir
              </Button>
            </div>
            <div className="flex flex-wrap items-end gap-3 p-4 border-b border-border bg-muted/30">
              <div className="space-y-1 min-w-[220px]">
                <Label className="label-eyebrow">Müşteri</Label>
                <Combobox
                  value={storagesCustomer}
                  onChange={setStoragesCustomer}
                  items={[
                    { value: "", label: "Tümü" },
                    ...customers.map((c) => ({
                      value: c.id,
                      label: `${c.full_name}${c.phone ? ` — ${c.phone}` : ""}`,
                      keywords: [
                        c.full_name,
                        c.phone,
                        ...(c.vehicles || []).map((v) => v.plate),
                      ].filter(Boolean),
                    })),
                  ]}
                  placeholder="Tümü"
                  searchPlaceholder="Ad, telefon veya plaka ara..."
                  emptyText="Müşteri bulunamadı"
                  testId="storages-customer-select"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-eyebrow">Sezon Tipi</Label>
                <Select value={storagesSeason} onValueChange={setStoragesSeason}>
                  <SelectTrigger className="w-32 rounded-sm h-9" data-testid="storages-season-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="yaz">Yaz</SelectItem>
                    <SelectItem value="kis">Kış</SelectItem>
                    <SelectItem value="dort_mevsim">4 Mevsim</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-eyebrow">Emanet T. (Baş.)</Label>
                <Input type="date" value={checkinFrom} onChange={(e) => setCheckinFrom(e.target.value)} className="rounded-sm h-9 w-36" data-testid="storages-checkin-from" />
              </div>
              <div className="space-y-1">
                <Label className="label-eyebrow">Emanet T. (Bit.)</Label>
                <Input type="date" value={checkinTo} onChange={(e) => setCheckinTo(e.target.value)} className="rounded-sm h-9 w-36" data-testid="storages-checkin-to" />
              </div>
              <div className="space-y-1">
                <Label className="label-eyebrow">Teslimat T. (Baş.)</Label>
                <Input type="date" value={checkoutFrom} onChange={(e) => setCheckoutFrom(e.target.value)} className="rounded-sm h-9 w-36" data-testid="storages-checkout-from" />
              </div>
              <div className="space-y-1">
                <Label className="label-eyebrow">Teslimat T. (Bit.)</Label>
                <Input type="date" value={checkoutTo} onChange={(e) => setCheckoutTo(e.target.value)} className="rounded-sm h-9 w-36" data-testid="storages-checkout-to" />
              </div>
              <Button onClick={applyStorageFilters} className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-9" data-testid="storages-apply">
                Uygula
              </Button>
              {hasStorageFilters && (
                <Button variant="outline" onClick={clearStorageFilters} className="rounded-sm h-9" data-testid="storages-clear">
                  Temizle
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-foreground text-background">
                  <tr>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Müşteri</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Plaka</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Marka / Ölçü</th>
                    <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Adet</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Sezon</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Depo / Raf</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">{dateColLabel}</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Durum</th>
                    <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Ücret</th>
                    <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Ödenen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {storagesLoading ? (
                    <tr>
                      <td colSpan={10} className="p-8 text-center text-muted-foreground text-sm">
                        Yükleniyor…
                      </td>
                    </tr>
                  ) : storages.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="p-8 text-center text-muted-foreground text-sm">
                        Kayıt yok
                      </td>
                    </tr>
                  ) : (
                    storages.map((s) => (
                      <tr key={s.id} data-testid={`storages-row-${s.id}`}>
                        <td className="px-4 py-2.5 font-semibold">{s.customer_name || "—"}</td>
                        <td className="px-4 py-2.5 font-mono">{s.plate || "—"}</td>
                        <td className="px-4 py-2.5">{tyreSummary(s)}</td>
                        <td className="px-4 py-2.5 font-mono text-right">{s.quantity ?? 0}</td>
                        <td className="px-4 py-2.5">{seasonLabel(s.season)}</td>
                        <td className="px-4 py-2.5 text-xs">
                          {(s.warehouse_name || "—") + " / " + (s.location || "—")}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs">
                          {storageDateCell(s)}
                        </td>
                        <td className="px-4 py-2.5">
                          {s.status === "depoda" ? "Depoda" : s.status === "teslim_edildi" ? "Teslim" : s.status}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-right">{fmt(s.fee)}</td>
                        <td className="px-4 py-2.5 font-mono text-right">{fmt(s.paid_amount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ------------------------ SATIŞ/STOK RAPORU ------------------------ */}
        {salesStockEnabled && (
          <TabsContent value="sales-stock" className="mt-4 space-y-4">
            <Card className="rounded-sm border-border overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
                <div>
                  <div className="label-eyebrow">Satış / Stok</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {ssLoading
                      ? "Yükleniyor…"
                      : `${ssData?.rows?.length || 0} ürün • ${ssFilterLabel()}`}
                  </div>
                </div>
                <Button
                  onClick={downloadSalesStockPdf}
                  disabled={ssPdfBusy || ssLoading || !(ssData?.rows || []).length}
                  className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                  data-testid="sales-stock-pdf-btn"
                >
                  {ssPdfBusy ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileDown className="h-4 w-4 mr-2" />
                  )}
                  PDF İndir
                </Button>
              </div>

              {/* Filtreler */}
              <div className="flex flex-wrap items-end gap-3 p-4 border-b border-border bg-muted/30">
                <div className="space-y-1">
                  <Label className="label-eyebrow">Ürün Tipi</Label>
                  <Select value={ssType} onValueChange={setSsType}>
                    <SelectTrigger className="w-40 rounded-sm h-9" data-testid="ss-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="lastik">Lastik</SelectItem>
                      <SelectItem value="jant">Jant</SelectItem>
                      <SelectItem value="diger">Diğer Malzeme</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="label-eyebrow">Durum</Label>
                  <Select value={ssCondition} onValueChange={setSsCondition}>
                    <SelectTrigger className="w-32 rounded-sm h-9" data-testid="ss-condition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="sifir">Sıfır</SelectItem>
                      <SelectItem value="ikinci_el">İkinci El</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Üretim Yılı: ürün alanı lastiğe özel olduğu için filtre de
                    YALNIZCA Ürün Tipi = Lastik seçiliyken gösterilir. */}
                {ssType === "lastik" && (
                  <div className="space-y-1">
                    <Label className="label-eyebrow">Üretim Yılı</Label>
                    <Input
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="2024"
                      value={ssYear}
                      onChange={(e) => setSsYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      className="rounded-sm h-9 w-24 font-mono"
                      data-testid="ss-production-year"
                    />
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="label-eyebrow">Çıkış Tipi</Label>
                  <Select value={ssExitType} onValueChange={setSsExitType}>
                    <SelectTrigger className="w-36 rounded-sm h-9" data-testid="ss-exit-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="satis">Satış</SelectItem>
                      <SelectItem value="iade">Ürün İade</SelectItem>
                      <SelectItem value="kullanim">Kullanım</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="label-eyebrow">Stok Durumu</Label>
                  <Select value={ssStockStatus} onValueChange={setSsStockStatus}>
                    <SelectTrigger className="w-28 rounded-sm h-9" data-testid="ss-stock-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="var">Var</SelectItem>
                      <SelectItem value="yok">Yok</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="label-eyebrow">Depo</Label>
                  <Select value={ssWarehouse} onValueChange={setSsWarehouse}>
                    <SelectTrigger className="w-40 rounded-sm h-9" data-testid="ss-warehouse">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      {ssWarehouses.map((w) => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Satıcı = stok girişi kaynağı */}
                <div className="space-y-1">
                  <Label className="label-eyebrow">Satıcı (Stok Girişi)</Label>
                  <Combobox
                    value={ssSupplier}
                    onChange={setSsSupplier}
                    items={supplierItems}
                    placeholder="Tümü"
                    searchPlaceholder="Satıcı ara..."
                    emptyText="Satıcı bulunamadı"
                    testId="ss-supplier"
                    className="w-52 h-9"
                  />
                </div>
                {/* Müşteri = stok çıkışı (satış) tarafı */}
                <div className="space-y-1">
                  <Label className="label-eyebrow">Müşteri (Stok Çıkışı)</Label>
                  <Combobox
                    value={ssCustomer}
                    onChange={setSsCustomer}
                    items={customerItems}
                    placeholder="Tümü"
                    searchPlaceholder="Ad, telefon veya plaka ara..."
                    emptyText="Müşteri bulunamadı"
                    testId="ss-customer"
                    className="w-52 h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="label-eyebrow">Tarih (Baş.)</Label>
                  <Input type="date" value={ssFrom} onChange={(e) => setSsFrom(e.target.value)} className="rounded-sm h-9 w-36" data-testid="ss-from" />
                </div>
                <div className="space-y-1">
                  <Label className="label-eyebrow">Tarih (Bit.)</Label>
                  <Input type="date" value={ssTo} onChange={(e) => setSsTo(e.target.value)} className="rounded-sm h-9 w-36" data-testid="ss-to" />
                </div>
                <Button
                  onClick={() => setSsReload((t) => t + 1)}
                  className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-9"
                  data-testid="ss-apply"
                >
                  Uygula
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSsType("all");
                    setSsCondition("all");
                    setSsYear("");
                    setSsExitType("all");
                    setSsStockStatus("all");
                    setSsWarehouse("all");
                    setSsSupplier("all");
                    setSsCustomer("all");
                    setSsFrom("");
                    setSsTo("");
                    setSsReload((t) => t + 1);
                  }}
                  className="rounded-sm h-9"
                  data-testid="ss-clear"
                >
                  Temizle
                </Button>
              </div>
              {ssCustomer !== "all" && (
                <div className="px-4 py-2 border-b border-border bg-sky-50/60 text-xs text-sky-900">
                  Müşteri filtresi aktif: satış adedi, satış tutarı, kâr ve tahsilat
                  kırılımı yalnızca bu müşterinin satışlarını gösterir. Giriş / iade /
                  kullanım kolonları ürünün kendi stok hareketlerini gösterir.
                </div>
              )}

              {/* Toplamlar */}
              {ssData?.totals && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-px bg-border">
                  {[
                    ["Ürün", ssData.totals.product_count],
                    ["Stok", ssData.totals.stock_quantity],
                    ["Kritik", ssData.totals.critical_count],
                    ["Giriş", ssData.totals.entry_quantity],
                    ["Satış", ssData.totals.sale_quantity],
                    ["İade", ssData.totals.return_quantity],
                    ["Kullanım", ssData.totals.usage_quantity],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-card p-3">
                      <div className="label-eyebrow">{k}</div>
                      <div className="font-display font-bold text-lg mt-0.5">{v ?? 0}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-foreground text-background">
                    <tr>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Ürün</th>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Tip</th>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Depo</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Stok</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Kritik</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Giriş</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Satış</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">İade</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Kullanım</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Satış Tutarı</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Kâr</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {ssLoading ? (
                      <tr><td colSpan={12} className="p-8 text-center text-muted-foreground text-sm">Yükleniyor…</td></tr>
                    ) : !(ssData?.rows || []).length ? (
                      <tr><td colSpan={12} className="p-8 text-center text-muted-foreground text-sm">Kayıt yok</td></tr>
                    ) : (
                      ssData.rows.map((r) => (
                        <tr
                          key={r.product_id}
                          className={r.is_critical ? "bg-red-50/60" : ""}
                          data-testid={`ss-row-${r.product_id}`}
                        >
                          <td className="px-4 py-2.5 font-semibold">{r.name}</td>
                          <td className="px-4 py-2.5 text-xs">{productTypeLabel(r.product_type)}</td>
                          <td className="px-4 py-2.5 text-xs font-mono">{r.warehouse_name}</td>
                          <td className="px-4 py-2.5 font-mono text-right font-bold">{r.quantity}</td>
                          <td className="px-4 py-2.5 font-mono text-right text-muted-foreground">{r.critical_quantity}</td>
                          <td className="px-4 py-2.5 font-mono text-right">{r.entry_quantity}</td>
                          <td className="px-4 py-2.5 font-mono text-right">{r.sale_quantity}</td>
                          <td className="px-4 py-2.5 font-mono text-right">{r.return_quantity}</td>
                          <td className="px-4 py-2.5 font-mono text-right">{r.usage_quantity}</td>
                          <td className="px-4 py-2.5 font-mono text-right">{fmt(r.sales_gross)}</td>
                          <td className="px-4 py-2.5 font-mono text-right">{fmt(r.profit_net)}</td>
                          <td className="px-4 py-2.5 text-right">
                            {/* Satış/Stok ekranıyla AYNI görüntüleme diyaloğu.
                                Rapor satırı ürünün tüm alanlarını taşımaz;
                                tıklanınca mevcut ürün ucundan çekilir (yetki
                                ve tenant kontrolü orada yapılır). */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-sm"
                              disabled={ssViewLoadingId === r.product_id}
                              onClick={() => openSsProduct(r.product_id)}
                              data-testid={`ss-view-${r.product_id}`}
                              aria-label="Görüntüle"
                              title="Görüntüle"
                            >
                              {ssViewLoadingId === r.product_id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Eye className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Tahsilat bilgileri */}
            <Card className="rounded-sm border-border p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="label-eyebrow">Satış Tahsilatları (Tahsilat Şekline Göre)</div>
                <div className="text-sm font-mono">
                  Toplam tahsil edilen: <b>{fmt(ssData?.totals?.collected)}</b>
                </div>
              </div>
              {!(ssData?.collections || []).length ? (
                <Empty msg="Bu aralıkta satış tahsilatı yok" />
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {ssData.collections.map((c) => (
                    <div
                      key={c.method}
                      className="border border-border rounded-sm p-3 flex items-center justify-between"
                      data-testid={`ss-collection-${c.method}`}
                    >
                      <div>
                        <div className="font-semibold">{paymentMethodLabel(c.method)}</div>
                        <div className="text-xs text-muted-foreground">{c.count} tahsilat</div>
                      </div>
                      <div className="font-mono font-bold">{fmt(c.total)}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Satış/Stok ekranıyla ortak görüntüleme diyaloğu */}
      <ProductViewDialog
        product={ssViewProduct}
        onClose={() => setSsViewProduct(null)}
        meta={ssMeta}
        currency={defaultCurrency}
      />
    </div>
  );
}

function productTypeLabel(t) {
  if (t === "lastik") return "Lastik";
  if (t === "jant") return "Jant";
  if (t === "diger") return "Diğer Malzeme";
  return t || "—";
}

function KPI({ label, value, icon: Icon, accent }) {
  return (
    <Card className="rounded-sm p-4 border-border">
      <div className="flex items-start justify-between">
        <div className="label-eyebrow">{label}</div>
        <div className={`h-8 w-8 rounded-sm flex items-center justify-center ${accent ? "bg-amber-500 text-foreground" : "bg-foreground text-background"}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="font-display font-black text-2xl tracking-tighter mt-2">{value}</div>
    </Card>
  );
}

function Empty({ msg }) {
  return <div className="text-center py-12 text-sm text-muted-foreground">{msg}</div>;
}

function seasonLabel(s) {
  if (s === "yaz") return "Yaz";
  if (s === "kis") return "Kış";
  if (s === "dort_mevsim") return "4 Mevsim";
  return "Yok";
}
